/**
 * IndexEntryCard - Card for displaying a single index channel entry
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as MatchedIcon,
  Warning as UnmatchedIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { ParsedIndexEntry, LibraryMatchResult, LibraryDecision } from '@/types/indexChannel';
import MessageViewIcon from '@/components/messages/MessageViewIcon';
import CreateLibraryDialog from './CreateLibraryDialog';
import { useCreateLibrary } from '@/hooks/useLibraries';
import { useCreateLibraryAlias } from '@/hooks/useLibraryAliases';
import { LibraryAliasType } from '@/types/enums';

interface Library {
  id: number;
  canonical_name: string;
}

interface IndexEntryCardProps {
  entry: ParsedIndexEntry;
  libraryMatch?: LibraryMatchResult;
  libraries: Library[];
  selected: boolean;
  decision?: LibraryDecision;
  onToggleSelect: (entryId: string) => void;
  onUpdateRange: (entryId: string, start: number, end: number) => void;
  onDecisionChange: (entryId: string, decision: LibraryDecision) => void;
  onViewMessage: (messageId: number, chatId: number) => void;
}

const IndexEntryCard: React.FC<IndexEntryCardProps> = ({
  entry,
  libraryMatch,
  libraries,
  selected,
  decision,
  onToggleSelect,
  onUpdateRange,
  onDecisionChange,
  onViewMessage,
}) => {
  const [isEditingRange, setIsEditingRange] = useState(false);
  const [editedStart, setEditedStart] = useState(entry.target_message_id || 0);
  const [editedEnd, setEditedEnd] = useState(entry.calculated_end_message_id || 0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const createLibraryMutation = useCreateLibrary();
  const createAliasMutation = useCreateLibraryAlias();

  const entryId = `${entry.index_no}-${entry.library_hashtag}`;
  const isMatched = libraryMatch?.matched_library_id !== null;
  const hasDecision = !!decision;

  const handleSaveRange = () => {
    onUpdateRange(entryId, editedStart, editedEnd);
    setIsEditingRange(false);
  };

  const handleCancelEdit = () => {
    setEditedStart(entry.target_message_id || 0);
    setEditedEnd(entry.calculated_end_message_id || 0);
    setIsEditingRange(false);
  };

  const handleActionChange = (action: 'create' | 'link' | 'skip') => {
    if (action === 'create') {
      // Open dialog to create library
      setCreateDialogOpen(true);
    } else if (action === 'skip') {
      onDecisionChange(entryId, { action: 'skip' });
    }
  };

  const handleCreateLibrarySubmit = async (data: {
    canonical_name: string;
    slug: string;
    notes: string;
    aliases: string[];
  }) => {
    try {
      // Create the library in the database
      const newLibrary = await createLibraryMutation.mutateAsync({
        canonical_name: data.canonical_name,
        slug: data.slug || data.canonical_name.toLowerCase().replace(/\s+/g, '-'),
        notes: data.notes || undefined,
      });

      // Create aliases for the library
      if (data.aliases.length > 0) {
        for (const aliasText of data.aliases) {
          try {
            const aliasType = aliasText.startsWith('#')
              ? LibraryAliasType.HASHTAG
              : LibraryAliasType.TEXT;

            await createAliasMutation.mutateAsync({
              libraryId: newLibrary.id,
              data: {
                alias: aliasText,
                alias_type: aliasType,
              },
            });
          } catch (error) {
            console.error(`Failed to create alias "${aliasText}":`, error);
            // Continue creating other aliases even if one fails
          }
        }
      }

      // Update decision to link to the newly created library
      onDecisionChange(entryId, {
        action: 'link',
        library_id: newLibrary.id,
      });

      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to create library:', error);
      alert('Failed to create library. Please try again.');
    }
  };

  const handleLibraryLink = (libraryId: number) => {
    onDecisionChange(entryId, {
      action: 'link',
      library_id: libraryId,
    });
  };

  return (
    <Card
      sx={{
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.paper',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={selected}
              onChange={() => onToggleSelect(entryId)}
              disabled={hasDecision}
            />
            <Typography variant="h6">
              Entry #{entry.index_no}
            </Typography>
          </Box>
          {isMatched ? (
            <Chip
              icon={<MatchedIcon />}
              label="Matched"
              color="success"
              size="small"
            />
          ) : (
            <Chip
              icon={<UnmatchedIcon />}
              label="No Match"
              color="warning"
              size="small"
            />
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Library Hashtag:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            {entry.library_hashtag}
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Manuscript Count:
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {entry.manuscript_count} manuscripts
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Country:
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {entry.country}
          </Typography>
        </Box>

        {/* Message Range */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Message Range:
            </Typography>
            {!isEditingRange && (
              <IconButton size="small" onClick={() => setIsEditingRange(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {isEditingRange ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Start"
                type="number"
                size="small"
                value={editedStart}
                onChange={(e) => setEditedStart(Number(e.target.value))}
                sx={{ flex: 1 }}
              />
              <Typography>→</Typography>
              <TextField
                label="End"
                type="number"
                size="small"
                value={editedEnd}
                onChange={(e) => setEditedEnd(Number(e.target.value))}
                sx={{ flex: 1 }}
              />
              <IconButton size="small" color="primary" onClick={handleSaveRange}>
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">
                {entry.target_message_id || 'Unknown'}
              </Typography>
              {entry.target_message_id && entry.target_chat_id && (
                <MessageViewIcon
                  messageId={entry.target_message_id}
                  chatId={entry.target_chat_id}
                  onViewMessage={onViewMessage}
                />
              )}
              <Typography>→</Typography>
              <Typography variant="body1">
                {entry.calculated_end_message_id || 'End'}
              </Typography>
              {entry.calculated_end_message_id && entry.target_chat_id && (
                <MessageViewIcon
                  messageId={entry.calculated_end_message_id}
                  chatId={entry.target_chat_id}
                  onViewMessage={onViewMessage}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Library Match Status */}
        {isMatched && libraryMatch ? (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2" color="success.dark" gutterBottom>
              ✓ Matched to existing library:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
              {libraryMatch.matched_library_name}
            </Typography>
            <Typography variant="caption" color="success.dark">
              Suggested action: {libraryMatch.suggested_action}
            </Typography>
          </Box>
        ) : decision ? (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark" gutterBottom>
              ✓ Decision made:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'info.dark' }}>
              {decision.action === 'create' && `Create new library: ${decision.canonical_name}`}
              {decision.action === 'link' && decision.library_id && (() => {
                const linkedLibrary = libraries.find(lib => lib.id === decision.library_id);
                return `Link to: ${linkedLibrary?.canonical_name || `Library ID ${decision.library_id}`}`;
              })()}
              {decision.action === 'skip' && 'Skip this entry'}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => onDecisionChange(entryId, { action: 'skip' })}
              sx={{ mt: 1, color: 'info.dark' }}
            >
              Change Decision
            </Button>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Action:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleActionChange('create')}
                disabled={createLibraryMutation.isPending}
                startIcon={createLibraryMutation.isPending ? <CircularProgress size={16} /> : undefined}
              >
                {createLibraryMutation.isPending ? 'Creating...' : 'Create New Library'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleActionChange('skip')}
              >
                Skip Entry
              </Button>
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel>Or Link to Existing Library</InputLabel>
              <Select
                label="Or Link to Existing Library"
                onChange={(e) => handleLibraryLink(Number(e.target.value))}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select a library...</em>
                </MenuItem>
                {libraries.map((lib) => (
                  <MenuItem key={lib.id} value={lib.id}>
                    {lib.canonical_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </CardContent>

      {/* Create Library Dialog */}
      <CreateLibraryDialog
        open={createDialogOpen}
        initialName={entry.library_hashtag?.replace('#', '') || 'New Library'}
        isLoading={createLibraryMutation.isPending || createAliasMutation.isPending}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateLibrarySubmit}
      />
    </Card>
  );
};

export default IndexEntryCard;
